/**
 * Practice-audio provider. Mounted ABOVE the Nótur/Texti tabs of the song page so switching tabs
 * never interrupts playback. Owns exactly ONE <audio> element (rebuilt only by the iOS watchdog).
 *
 * - src is assigned right before play() (lazily, and again after the app was backgrounded while
 *   paused) — keeps play() inside the user gesture and avoids iOS 26 "silent audio" elements.
 * - Watchdog: after play() resolves (user play, visibilitychange→visible, pageshow) the element must
 *   make progress within ~1 s, otherwise it is rebuilt, seeked back and played again.
 * - blob: URLs from an offline resolver are revoked only after src changed and 'emptied' fired.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SongFileDTO } from '../../shared/dto/library';
import { useT } from '../i18n';
import { PREF, readPref, writePref } from '../viewer/storage';
import { resolveAudioSource, resolveAudioSourceSync, type AudioSourceResolver } from './audioSource';
import { clampRate, loopSeekTarget, NO_LOOP, resumePosition, setLoopPoint, SKIP_SECONDS, type AbLoop } from './logic';
import { setSessionHandlers, setSessionMetadata, setSessionPlaybackState, setSessionPosition } from './mediaSession';

export type AudioError = 'offline' | 'failed' | null;

export interface AudioControls {
  tracks: SongFileDTO[];
  current: SongFileDTO | null;
  playing: boolean;
  buffering: boolean;
  rate: number;
  loop: AbLoop;
  error: AudioError;
  select: (id: string, opts?: { play?: boolean }) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (t: number) => void;
  skip: (deltaSeconds: number) => void;
  setRate: (r: number) => void;
  markA: () => void;
  markB: () => void;
  clearLoop: () => void;
  next: () => void;
  previous: () => void;
  retry: () => void;
}

export interface AudioTime {
  time: number;
  duration: number;
}

const ControlsCtx = createContext<AudioControls | null>(null);
const TimeCtx = createContext<AudioTime>({ time: 0, duration: 0 });

export function useAudio(): AudioControls {
  const c = useContext(ControlsCtx);
  if (!c) throw new Error('AudioProvider missing');
  return c;
}

/** Same as useAudio() but null outside an AudioProvider (shared chrome such as the top bar). */
export function useAudioMaybe(): AudioControls | null {
  return useContext(ControlsCtx);
}

/** Current time / duration (updates ~4×/s while playing — only the dock subscribes). */
export function useAudioTime(): AudioTime {
  return useContext(TimeCtx);
}

interface SavedPos {
  t: number;
  rev: number;
}
const isSavedPos = (v: unknown): v is SavedPos =>
  !!v && typeof v === 'object' && typeof (v as SavedPos).t === 'number' && typeof (v as SavedPos).rev === 'number';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function savedStart(track: SongFileDTO): number {
  const p = readPref<SavedPos | null>(PREF.audioPos(track.id), null, (v): v is SavedPos | null => v === null || isSavedPos(v));
  if (!p || p.rev !== track.rev) return 0;
  return resumePosition(p.t, track.durationMs ? track.durationMs / 1000 : null);
}

function setPitch(a: HTMLAudioElement) {
  a.preservesPitch = true;
  (a as HTMLAudioElement & { webkitPreservesPitch?: boolean }).webkitPreservesPitch = true;
  (a as HTMLAudioElement & { mozPreservesPitch?: boolean }).mozPreservesPitch = true;
}

const isBlob = (u: string | null | undefined): u is string => !!u && u.startsWith('blob:');
const trackKey = (f: Pick<SongFileDTO, 'id' | 'rev'>) => `${f.id}:${f.rev}`;

export function AudioProvider({
  songId,
  songTitle,
  tracks,
  audioSource,
  onSourceError,
  children,
}: {
  songId: string;
  songTitle: string;
  /** Visible audio files, already ordered (own section first). */
  tracks: SongFileDTO[];
  /** Optional per-provider source resolver (default: the global one from audioSource.ts). */
  audioSource?: AudioSourceResolver;
  /** The current source failed while online (e.g. a newer revision replaced it): refetch the song. */
  onSourceError?: () => void;
  children: ReactNode;
}) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const elRef = useRef<HTMLAudioElement | null>(null);
  const unwireRef = useRef<(() => void) | null>(null);
  /** What the element plays / what the resolver answered, keyed by file id + rev. */
  const assignedRef = useRef<{ key: string; url: string } | null>(null);
  const resolvedRef = useRef<{ key: string; url: string } | null>(null);
  /** The one in-flight resolver call (shared by the pre-resolve effect and play()). */
  const pendingResolveRef = useRef<{ key: string; promise: Promise<string | null> } | null>(null);
  /** Bumped on unmount: late resolver answers from an older mount are discarded (and revoked). */
  const genRef = useRef(0);
  const retiredRef = useRef(new Set<string>());
  /** Position per track id while the page is open (seeded lazily from the remembered position). */
  const startPosRef = useRef(new Map<string, number>());
  const pendingSeekRef = useRef<number | null>(null);
  const wantPlayRef = useRef(false);
  const staleRef = useRef(false);
  const progressRef = useRef(0);
  const lastTimeRef = useRef(-1);
  const lastSavedRef = useRef(0);
  const lastRebuildRef = useRef(0);
  const sourceRef = useRef(audioSource);
  sourceRef.current = audioSource;
  const onSourceErrorRef = useRef(onSourceError);
  onSourceErrorRef.current = onSourceError;

  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;

  const [currentId, setCurrentId] = useState<string | null>(() => {
    const last = readPref<string | null>(PREF.lastTrack(songId), null, (v): v is string | null => v === null || typeof v === 'string');
    return tracks.find((x) => x.id === last)?.id ?? tracks[0]?.id ?? null;
  });
  const current = tracks.find((x) => x.id === currentId) ?? null;
  const currentRef = useRef(current);
  currentRef.current = current;

  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState<AudioError>(null);
  const [rate, setRateState] = useState(() => clampRate(readPref(PREF.audioRate(songId), 1, isNum)));
  const rateRef = useRef(rate);
  const [loop, setLoop] = useState<AbLoop>(NO_LOOP);
  const loopRef = useRef(loop);
  loopRef.current = loop;
  const [time, setTime] = useState(() => (current ? savedStart(current) : 0));
  const [duration, setDuration] = useState(() => (current?.durationMs ? current.durationMs / 1000 : 0));
  const durationRef = useRef(duration);
  durationRef.current = duration;

  /* ── helpers ─────────────────────────────────────────────────────────── */

  const isAssignedCurrent = () => !!assignedRef.current && !!currentRef.current && assignedRef.current.key === trackKey(currentRef.current);

  /** Position of a track that is not playing: last known this session, else the remembered one. */
  const startOf = useCallback((tr: SongFileDTO): number => {
    let s = startPosRef.current.get(tr.id);
    if (s === undefined) {
      s = savedStart(tr);
      startPosRef.current.set(tr.id, s);
    }
    return s;
  }, []);

  const currentTime = useCallback((): number => {
    const a = elRef.current;
    const tr = currentRef.current;
    if (!tr) return 0;
    if (a && isAssignedCurrent()) return a.currentTime;
    return startOf(tr);
  }, [startOf]);

  const savePosition = useCallback(() => {
    const tr = currentRef.current;
    if (!tr) return;
    // Nothing known about this track yet this session: leave the remembered position untouched.
    if (!isAssignedCurrent() && !startPosRef.current.has(tr.id)) return;
    const pos = currentTime();
    writePref(PREF.audioPos(tr.id), pos > 0 ? { t: pos, rev: tr.rev } : null);
    lastSavedRef.current = pos;
  }, [currentTime]);

  const updateSessionPosition = useCallback(() => {
    const a = elRef.current;
    const d = a && isAssignedCurrent() && Number.isFinite(a.duration) ? a.duration : durationRef.current;
    setSessionPosition(d, currentTime(), rateRef.current);
  }, [currentTime]);

  const revokeRetired = useCallback(() => {
    const keep = assignedRef.current?.url;
    for (const u of retiredRef.current) {
      if (u !== keep && u !== resolvedRef.current?.url) {
        URL.revokeObjectURL(u);
        retiredRef.current.delete(u);
      }
    }
  }, []);

  /* ── element lifecycle ───────────────────────────────────────────────── */

  const wire = useCallback(
    (a: HTMLAudioElement): (() => void) => {
      const on: Record<string, () => void> = {
        loadedmetadata: () => {
          if (pendingSeekRef.current !== null) {
            try {
              a.currentTime = pendingSeekRef.current;
            } catch {
              /* ignore */
            }
            pendingSeekRef.current = null;
          }
          if (isAssignedCurrent() && Number.isFinite(a.duration) && a.duration > 0) setDuration(a.duration);
          updateSessionPosition();
        },
        durationchange: () => {
          if (isAssignedCurrent() && Number.isFinite(a.duration) && a.duration > 0) setDuration(a.duration);
        },
        timeupdate: () => {
          const now = a.currentTime;
          if (now !== lastTimeRef.current) {
            progressRef.current++;
            lastTimeRef.current = now;
          }
          // A different track was selected but not played yet: its time is shown, not this one.
          if (!isAssignedCurrent()) return;
          const target = loopSeekTarget(now, loopRef.current);
          if (target !== null && !a.paused) {
            a.currentTime = target;
            setTime(target);
            return;
          }
          setTime(now);
          const tr = currentRef.current;
          if (tr) startPosRef.current.set(tr.id, now);
          if (Math.abs(now - lastSavedRef.current) >= 5) savePosition();
        },
        play: () => {
          setPlaying(true);
          setError(null);
          setSessionPlaybackState('playing');
        },
        playing: () => {
          setBuffering(false);
          updateSessionPosition();
        },
        waiting: () => setBuffering(true),
        canplay: () => setBuffering(false),
        pause: () => {
          setPlaying(false);
          setBuffering(false);
          setSessionPlaybackState('paused');
          savePosition();
        },
        ended: () => {
          wantPlayRef.current = false;
          setPlaying(false);
          setSessionPlaybackState('paused');
          const tr = currentRef.current;
          if (tr) {
            startPosRef.current.set(tr.id, 0);
            writePref(PREF.audioPos(tr.id), null);
          }
        },
        seeked: () => updateSessionPosition(),
        ratechange: () => updateSessionPosition(),
        error: () => {
          if (!assignedRef.current) return;
          wantPlayRef.current = false;
          setPlaying(false);
          setBuffering(false);
          setError(navigator.onLine ? 'failed' : 'offline');
          if (navigator.onLine) onSourceErrorRef.current?.();
        },
        emptied: () => revokeRetired(),
      };
      for (const [k, fn] of Object.entries(on)) a.addEventListener(k, fn);
      return () => {
        for (const [k, fn] of Object.entries(on)) a.removeEventListener(k, fn);
      };
    },
    [revokeRetired, savePosition, updateSessionPosition],
  );

  const makeElement = useCallback((): HTMLAudioElement => {
    const a = document.createElement('audio');
    a.preload = 'metadata';
    setPitch(a);
    a.defaultPlaybackRate = rateRef.current;
    a.playbackRate = rateRef.current;
    containerRef.current?.appendChild(a);
    unwireRef.current = wire(a);
    elRef.current = a;
    return a;
  }, [wire]);

  const destroyElement = useCallback((a: HTMLAudioElement) => {
    if (elRef.current === a) {
      unwireRef.current?.();
      unwireRef.current = null;
      elRef.current = null;
    }
    a.pause();
    a.removeAttribute('src');
    try {
      a.load();
    } catch {
      /* ignore */
    }
    a.remove();
  }, []);

  const assignSrc = useCallback((a: HTMLAudioElement, track: SongFileDTO, url: string, startAt: number) => {
    const prev = assignedRef.current;
    if (prev && prev.url !== url && isBlob(prev.url)) retiredRef.current.add(prev.url);
    assignedRef.current = { key: trackKey(track), url };
    staleRef.current = false;
    pendingSeekRef.current = startAt > 0 ? startAt : null;
    lastTimeRef.current = -1;
    setSessionMetadata({ title: songTitle, artist: `${t('app.name')} – ${track.title}`, album: t('app.name') });
    a.src = url;
    if (startAt > 0) {
      try {
        a.currentTime = startAt;
      } catch {
        /* applied on loadedmetadata */
      }
    }
  }, [songTitle, t]);

  /* ── watchdog ────────────────────────────────────────────────────────── */

  const rebuildRef = useRef<(at: number) => void>(() => undefined);

  const runWatchdog = useCallback((a: HTMLAudioElement) => {
    const p0 = progressRef.current;
    setTimeout(() => {
      if (elRef.current !== a || a.paused || !wantPlayRef.current || a.seeking || a.readyState < 3) return;
      if (document.visibilityState !== 'visible') return;
      if (progressRef.current !== p0) return;
      if (Date.now() - lastRebuildRef.current < 10_000) return;
      lastRebuildRef.current = Date.now();
      rebuildRef.current(a.currentTime);
    }, 1100);
  }, []);

  const doPlay = useCallback(
    (a: HTMLAudioElement) => {
      wantPlayRef.current = true;
      a.play().then(
        () => runWatchdog(a),
        (e: unknown) => {
          const name = (e as Error)?.name;
          if (name === 'AbortError') return;
          wantPlayRef.current = false;
          setPlaying(false);
          setBuffering(false);
          if (name !== 'NotAllowedError') setError(navigator.onLine ? 'failed' : 'offline');
        },
      );
    },
    [runWatchdog],
  );

  rebuildRef.current = (at: number) => {
    const old = elRef.current;
    const tr = currentRef.current;
    const asg = assignedRef.current;
    if (!old || !tr || !asg || asg.key !== trackKey(tr)) return;
    destroyElement(old);
    const a = makeElement();
    assignedRef.current = null; // same URL is re-used; nothing to retire
    assignSrc(a, tr, asg.url, at);
    doPlay(a);
  };

  /* ── public API ──────────────────────────────────────────────────────── */

  /**
   * Resolves a track's source through the (offline) resolver. One call per track key is in flight
   * at a time and its answer is stored in resolvedRef only — so a blob: URL has exactly one owner
   * and is revoked when it is replaced, when the track changed meanwhile, or after unmount.
   * Resolves with null when the answer was discarded.
   */
  const ensureResolved = useCallback((track: SongFileDTO): Promise<string | null> => {
    const key = trackKey(track);
    if (resolvedRef.current?.key === key) return Promise.resolve(resolvedRef.current.url);
    const inflight = pendingResolveRef.current;
    if (inflight?.key === key) return inflight.promise;
    const gen = genRef.current;
    const promise = resolveAudioSource(track, sourceRef.current).then((u): string | null => {
      if (pendingResolveRef.current?.promise === promise) pendingResolveRef.current = null;
      const cur = currentRef.current;
      if (gen !== genRef.current || !cur || trackKey(cur) !== key) {
        if (isBlob(u) && u !== assignedRef.current?.url) URL.revokeObjectURL(u);
        return null;
      }
      const old = resolvedRef.current;
      if (old && old.url !== u && isBlob(old.url) && old.url !== assignedRef.current?.url) URL.revokeObjectURL(old.url);
      resolvedRef.current = { key, url: u };
      return u;
    });
    pendingResolveRef.current = { key, promise };
    return promise;
  }, []);

  const play = useCallback(() => {
    const tr = currentRef.current;
    if (!tr) return;
    const a = elRef.current ?? makeElement();
    setError(null);
    if (isAssignedCurrent() && !staleRef.current) {
      doPlay(a);
      return;
    }
    const elPos = isAssignedCurrent() ? a.currentTime : 0;
    const startAt = elPos > 0 ? elPos : startOf(tr);
    const sync = sourceRef.current ? null : resolveAudioSourceSync(tr);
    const url = isAssignedCurrent() ? assignedRef.current!.url : resolvedRef.current?.key === trackKey(tr) ? resolvedRef.current.url : sync;
    if (url) {
      assignSrc(a, tr, url, startAt);
      doPlay(a);
      return;
    }
    // An offline resolver is still working: play as soon as it answers (it keeps the URL either way).
    wantPlayRef.current = true;
    setBuffering(true);
    void ensureResolved(tr).then((u) => {
      if (!u || currentRef.current?.id !== tr.id || !wantPlayRef.current || elRef.current !== a) {
        if (!wantPlayRef.current) setBuffering(false); // paused before the resolver answered
        return;
      }
      assignSrc(a, tr, u, startAt);
      doPlay(a);
    });
  }, [assignSrc, doPlay, ensureResolved, makeElement, startOf]);

  const pause = useCallback(() => {
    wantPlayRef.current = false;
    elRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    const a = elRef.current;
    if (a && !a.paused) pause();
    else play();
  }, [pause, play]);

  const seek = useCallback(
    (target: number) => {
      const tr = currentRef.current;
      if (!tr) return;
      const a = elRef.current;
      const d = a && isAssignedCurrent() && Number.isFinite(a.duration) ? a.duration : durationRef.current;
      const tt = Math.max(0, d > 0 ? Math.min(d - 0.05, target) : target);
      if (a && isAssignedCurrent()) {
        if (a.readyState >= 1) a.currentTime = tt;
        else pendingSeekRef.current = tt;
      }
      startPosRef.current.set(tr.id, tt);
      setTime(tt);
      writePref(PREF.audioPos(tr.id), tt > 0 ? { t: tt, rev: tr.rev } : null);
      lastSavedRef.current = tt;
      updateSessionPosition();
    },
    [updateSessionPosition],
  );

  const skip = useCallback((delta: number) => seek(currentTime() + delta), [currentTime, seek]);

  const setRate = useCallback(
    (r: number) => {
      const v = clampRate(r);
      rateRef.current = v;
      setRateState(v);
      writePref(PREF.audioRate(songId), v === 1 ? null : v);
      const a = elRef.current;
      if (a) {
        setPitch(a);
        a.defaultPlaybackRate = v;
        a.playbackRate = v;
      }
    },
    [songId],
  );

  const markA = useCallback(() => setLoop((l) => setLoopPoint(l, 'a', currentTime(), durationRef.current)), [currentTime]);
  const markB = useCallback(() => setLoop((l) => setLoopPoint(l, 'b', currentTime(), durationRef.current)), [currentTime]);
  const clearLoop = useCallback(() => setLoop(NO_LOOP), []);

  const select = useCallback(
    (id: string, opts?: { play?: boolean }) => {
      const tr = tracksRef.current.find((x) => x.id === id);
      if (!tr) return;
      const a = elRef.current;
      const wasPlaying = !!a && !a.paused;
      if (tr.id !== currentRef.current?.id) {
        savePosition();
        if (a && !a.paused) a.pause();
        wantPlayRef.current = false;
        currentRef.current = tr;
        setCurrentId(tr.id);
        writePref(PREF.lastTrack(songId), tr.id);
        setLoop(NO_LOOP);
        setError(null);
        const start = startOf(tr);
        setTime(start);
        setDuration(tr.durationMs ? tr.durationMs / 1000 : 0);
      }
      if (opts?.play ?? wasPlaying) play();
    },
    [play, savePosition, songId, startOf],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      const list = tracksRef.current;
      const i = list.findIndex((x) => x.id === currentRef.current?.id);
      const next = list[(i + dir + list.length) % list.length];
      if (next) select(next.id);
    },
    [select],
  );
  const next = useCallback(() => step(1), [step]);
  const previous = useCallback(() => step(-1), [step]);

  const retry = useCallback(() => {
    staleRef.current = true;
    setError(null);
    play();
  }, [play]);

  /* ── effects ─────────────────────────────────────────────────────────── */

  // One <audio> element for the lifetime of the song page.
  useEffect(() => {
    const a = makeElement();
    return () => {
      savePosition();
      wantPlayRef.current = false;
      destroyElement(a);
      genRef.current++;
      pendingResolveRef.current = null;
      for (const u of new Set([assignedRef.current?.url, resolvedRef.current?.url, ...retiredRef.current])) if (isBlob(u)) URL.revokeObjectURL(u);
      retiredRef.current.clear();
      assignedRef.current = null;
      resolvedRef.current = null;
      setPlaying(false);
      setSessionPlaybackState('none');
      setSessionMetadata(null);
    };
  }, [destroyElement, makeElement, savePosition]);

  // The current track disappeared (e.g. "Allar skrár" switched off): fall back to the first one.
  useEffect(() => {
    if (current || tracks.length === 0) return;
    const first = tracks[0]!;
    const a = elRef.current;
    if (a && !a.paused) a.pause();
    wantPlayRef.current = false;
    const start = startOf(first);
    setCurrentId(first.id);
    setLoop(NO_LOOP);
    setTime(start);
    setDuration(first.durationMs ? first.durationMs / 1000 : 0);
  }, [current, tracks, startOf]);

  // Pre-resolve the current track's source (offline resolvers are async). The answer is kept in
  // resolvedRef by ensureResolved(); a superseded track's unused blob: URL is revoked here.
  useEffect(() => {
    if (!current) return;
    if (resolvedRef.current?.key === trackKey(current)) return;
    const old = resolvedRef.current;
    if (old && isBlob(old.url) && old.url !== assignedRef.current?.url) URL.revokeObjectURL(old.url);
    resolvedRef.current = null;
    if (!sourceRef.current && resolveAudioSourceSync(current)) return;
    void ensureResolved(current);
  }, [current, ensureResolved]);

  // A–B loop precision: check every frame while playing and visible (timeupdate is only ~4 Hz).
  useEffect(() => {
    if (!playing || loop.a === null || loop.b === null) return;
    let raf = 0;
    const tick = () => {
      const a = elRef.current;
      if (a && !a.paused) {
        const target = loopSeekTarget(a.currentTime, loopRef.current);
        if (target !== null) a.currentTime = target;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, loop]);

  // Background / foreground handling + iOS watchdog.
  useEffect(() => {
    const onVis = () => {
      const a = elRef.current;
      if (document.visibilityState === 'hidden') {
        savePosition();
        if (a?.paused) staleRef.current = true;
        return;
      }
      if (a && wantPlayRef.current && !a.paused) {
        a.play().then(
          () => runWatchdog(a),
          () => undefined,
        );
      }
    };
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) staleRef.current = !!elRef.current?.paused;
      onVis();
    };
    const onHide = () => savePosition();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pageshow', onShow);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onShow);
      window.removeEventListener('pagehide', onHide);
    };
  }, [runWatchdog, savePosition]);

  // Media Session metadata + actions.
  useEffect(() => {
    if (!current) return;
    setSessionMetadata({ title: songTitle, artist: `${t('app.name')} – ${current.title}`, album: t('app.name') });
  }, [current, songTitle, t]);

  const apiRef = useRef({ play, pause, seek, skip, next, previous, currentTime });
  apiRef.current = { play, pause, seek, skip, next, previous, currentTime };
  useEffect(() => {
    setSessionHandlers({
      play: () => apiRef.current.play(),
      pause: () => apiRef.current.pause(),
      seekbackward: (d) => apiRef.current.skip(-(d.seekOffset ?? SKIP_SECONDS)),
      seekforward: (d) => apiRef.current.skip(d.seekOffset ?? SKIP_SECONDS),
      seekto: (d) => {
        if (typeof d.seekTime === 'number') apiRef.current.seek(d.seekTime);
      },
      previoustrack: () => apiRef.current.previous(),
      nexttrack: () => apiRef.current.next(),
    });
    return () => setSessionHandlers(null);
  }, []);

  useEffect(() => {
    updateSessionPosition();
  }, [duration, rate, updateSessionPosition]);

  const controls = useMemo<AudioControls>(
    () => ({
      tracks,
      current,
      playing,
      buffering,
      rate,
      loop,
      error,
      select,
      play,
      pause,
      toggle,
      seek,
      skip,
      setRate,
      markA,
      markB,
      clearLoop,
      next,
      previous,
      retry,
    }),
    [tracks, current, playing, buffering, rate, loop, error, select, play, pause, toggle, seek, skip, setRate, markA, markB, clearLoop, next, previous, retry],
  );
  const timeValue = useMemo<AudioTime>(() => ({ time, duration }), [time, duration]);

  return (
    <ControlsCtx.Provider value={controls}>
      <TimeCtx.Provider value={timeValue}>
        {children}
        <div ref={containerRef} hidden aria-hidden />
      </TimeCtx.Provider>
    </ControlsCtx.Provider>
  );
}
